// Asistente "Solicitar un módulo o función nueva": una pregunta por pantalla,
// en lenguaje sencillo (nada de "campo", "tipo de dato", etc.), con
// autoguardado como borrador en cada paso y un resumen final editable antes
// de enviar. Ver backend/app/models/solicitudes_wizard.py para las reglas.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, Checkbox, Radio, Spin, Alert, Upload, Tag, Progress } from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { notify } from "../../components/ui/notify";
import { wizardService } from "../../components/solicitudes-cambio/wizardService";

import "../../components/tribunal/TribunalPage.css";
import "../../components/solicitudes-cambio/solicitarCambio.css";

const { TextArea } = Input;

const TOTAL_PASOS = 10;

const MODULO_LABELS = {
  conciliacion: "Centro de Conciliación", tribunal: "Tribunal", junta: "Junta de Conciliación",
  desvinculaciones: "Desvinculaciones", abogados: "Abogados", agenda: "Agenda", informes: "Informes",
  autoridades: "Autoridades", configuracion: "Configuración", corresponsales: "Corresponsales",
  empresas: "Empresas", usuarios: "Usuarios", programador: "Programador",
};

function NoSeCheckbox({ checked, onChange }) {
  return (
    <label className="sc-nose-check">
      <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>No sé, decídelo tú</span>
    </label>
  );
}

function PasoShell({ numero, titulo, ejemplo, children, noSe, onNoSe, ocultarNoSe }) {
  return (
    <div className="sc-wizard-paso">
      <h2 className="sc-wizard-pregunta">{titulo}</h2>
      {ejemplo && <p className="sc-wizard-ejemplo">Ejemplo: {ejemplo}</p>}
      <div className={noSe ? "sc-wizard-contenido sc-wizard-contenido--apagado" : "sc-wizard-contenido"}>
        {children}
      </div>
      {!ocultarNoSe && <NoSeCheckbox checked={!!noSe} onChange={onNoSe} />}
    </div>
  );
}

export default function NuevaFuncionWizardPage() {
  const navigate = useNavigate();
  const { idWizard: idWizardParam } = useParams();

  const [idWizard, setIdWizard] = useState(idWizardParam ? Number(idWizardParam) : null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [paso, setPaso] = useState(1); // 1..10, 11 = resumen
  const [respuestas, setRespuestas] = useState({});
  const [archivos, setArchivos] = useState([]);
  const [catalogo, setCatalogo] = useState({ modulos: [], usuarios: [] });
  const [resumen, setResumen] = useState("");
  const [enviado, setEnviado] = useState(null);

  // Crea el borrador si llegamos sin id, luego navega a la URL con el id
  // (para poder recargar/compartir y seguir donde se quedó).
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        if (!idWizardParam) {
          const nuevo = await wizardService.crearBorrador();
          if (cancelado) return;
          navigate(`/solicitudes/nueva-funcion/${nuevo.id_wizard}`, { replace: true });
          return;
        }
        const [borrador, cat] = await Promise.all([
          wizardService.obtenerBorrador(idWizardParam),
          wizardService.obtenerCatalogo(),
        ]);
        if (cancelado) return;
        setIdWizard(borrador.id_wizard);
        setRespuestas(borrador.respuestas || {});
        setArchivos(borrador.archivos || []);
        setPaso(Math.min(borrador.paso_actual || 1, TOTAL_PASOS + 1));
        setCatalogo(cat);
      } catch (err) {
        if (!cancelado) notify.error("No se pudo abrir el asistente. Inténtalo de nuevo.");
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idWizardParam]);

  const respuestaActual = respuestas[`p${paso}`] || {};
  const setRespuestaActual = (patch) => {
    setRespuestas((prev) => ({ ...prev, [`p${paso}`]: { ...(prev[`p${paso}`] || {}), ...patch } }));
  };

  const puedeAvanzar = useMemo(() => {
    if (paso === 1) return !!(respuestaActual.nombre || "").trim();
    return true;
  }, [paso, respuestaActual]);

  const guardarPasoActual = useCallback(async () => {
    if (!idWizard) return;
    setGuardando(true);
    try {
      await wizardService.guardarPaso(idWizard, paso, respuestas[`p${paso}`] || {});
    } catch (err) {
      notify.error("No se pudo guardar tu avance. Revisa tu conexión.");
      throw err;
    } finally {
      setGuardando(false);
    }
  }, [idWizard, paso, respuestas]);

  const siguiente = async () => {
    if (!puedeAvanzar) {
      notify.warning("Cuéntanos al menos de qué quieres llevar control.");
      return;
    }
    try {
      await guardarPasoActual();
    } catch {
      return;
    }
    if (paso === TOTAL_PASOS) {
      try {
        const r = await wizardService.previsualizarResumen(idWizard);
        setResumen(r);
      } catch {
        // si falla la previsualizacion, el resumen se vuelve a pedir en la pantalla final
      }
    }
    setPaso((p) => Math.min(p + 1, TOTAL_PASOS + 1));
  };

  const atras = async () => {
    try { await guardarPasoActual(); } catch { /* seguimos igual */ }
    setPaso((p) => Math.max(1, p - 1));
  };

  const irAPregunta = (n) => setPaso(n);

  const subirArchivo = async (file) => {
    try {
      const subido = await wizardService.subirArchivo(idWizard, file);
      setArchivos((prev) => [...prev, subido]);
    } catch (err) {
      notify.error(err?.response?.data?.detail || "No se pudo subir el archivo.");
    }
    return false; // evita que antd intente subirlo por su cuenta
  };

  const quitarArchivo = async (idArchivo) => {
    await wizardService.eliminarArchivo(idWizard, idArchivo);
    setArchivos((prev) => prev.filter((a) => a.id !== idArchivo));
  };

  const enviarSolicitud = async () => {
    setEnviando(true);
    try {
      const resultado = await wizardService.enviar(idWizard);
      setEnviado(resultado);
      notify.success(`Solicitud ${resultado.folio} enviada. Te avisaremos cuando esté en revisión.`);
    } catch (err) {
      notify.error(err?.response?.data?.detail || "No se pudo enviar la solicitud. Inténtalo de nuevo.");
    } finally {
      setEnviando(false);
    }
  };

  if (cargando) {
    return <div className="text-center py-5"><Spin size="large" /></div>;
  }

  if (enviado) {
    return (
      <main className="trib-main">
        <div className="trib-wrap" style={{ maxWidth: 640, margin: "0 auto" }}>
          <div className="sc-wizard-paso text-center">
            <h2 className="sc-wizard-pregunta">¡Listo! Enviamos tu solicitud</h2>
            <p>Folio <strong>{enviado.folio}</strong>. Te avisaremos cuando esté en revisión.</p>
            <Button type="primary" onClick={() => navigate("/mis-solicitudes")}>Ir a Mis solicitudes</Button>
          </div>
        </div>
      </main>
    );
  }

  const esResumen = paso === TOTAL_PASOS + 1;

  return (
    <main className="trib-main">
      <div className="trib-wrap" style={{ maxWidth: 760, margin: "0 auto" }}>
        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="small text-secondary">
              {esResumen ? "Revisión final" : `Pregunta ${paso} de ${TOTAL_PASOS}`}
            </span>
            {guardando && <span className="small text-secondary">Guardando…</span>}
          </div>
          <Progress
            percent={Math.round((Math.min(paso, TOTAL_PASOS + 1) / (TOTAL_PASOS + 1)) * 100)}
            showInfo={false}
            strokeColor="#0A539A"
          />
        </div>

        {!esResumen ? (
          <>
            {paso === 1 && (
              <PasoShell numero={1} titulo="¿De qué quieres llevar control?" ejemplo="Mis proveedores y lo que les compro" ocultarNoSe>
                <Input
                  size="large"
                  placeholder="Nombre corto"
                  value={respuestaActual.nombre || ""}
                  onChange={(e) => setRespuestaActual({ nombre: e.target.value })}
                  maxLength={80}
                  showCount
                  className="mb-2"
                />
                <TextArea
                  rows={3}
                  placeholder="Cuéntanos un poco más (opcional)"
                  value={respuestaActual.descripcion || ""}
                  onChange={(e) => setRespuestaActual({ descripcion: e.target.value })}
                />
              </PasoShell>
            )}

            {paso === 2 && (
              <PasoShell
                numero={2}
                titulo="¿Para qué lo necesitas? ¿Qué problema te resuelve?"
                ejemplo="Hoy lo apunto en una libreta y se me pierde"
                noSe={respuestaActual.no_se}
                onNoSe={(v) => setRespuestaActual({ no_se: v })}
              >
                <TextArea
                  rows={4}
                  placeholder="Escribe aquí..."
                  value={respuestaActual.texto || ""}
                  onChange={(e) => setRespuestaActual({ texto: e.target.value })}
                  disabled={respuestaActual.no_se}
                />
              </PasoShell>
            )}

            {paso === 3 && (
              <PasoShell
                numero={3}
                titulo="¿Cómo lo llevas hoy? Mándanos una foto de tu libreta, formato o Excel."
                ejemplo="Una foto de tu cuaderno o una captura de tu Excel"
                noSe={respuestaActual.no_se}
                onNoSe={(v) => setRespuestaActual({ no_se: v })}
              >
                <p className="sc-hint">Con esto entendemos mucho más rápido lo que necesitas. Es opcional.</p>
                <Upload beforeUpload={subirArchivo} showUploadList={false} accept="image/*,.pdf,.xls,.xlsx" disabled={respuestaActual.no_se}>
                  <Button icon={<UploadOutlined />} disabled={respuestaActual.no_se}>Subir archivo</Button>
                </Upload>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {archivos.map((a) => (
                    <Tag key={a.id} closable onClose={() => quitarArchivo(a.id)} closeIcon={<DeleteOutlined />}>
                      {a.nombre_original || "Archivo"}
                    </Tag>
                  ))}
                </div>
              </PasoShell>
            )}

            {paso === 4 && (
              <PasoShell
                numero={4}
                titulo="Cuando anotas uno, ¿qué datos apuntas?"
                ejemplo="nombre, teléfono, qué me vende, cada cuándo le pido"
                noSe={respuestaActual.no_se}
                onNoSe={(v) => setRespuestaActual({ no_se: v })}
              >
                <DatosDinamicos
                  datos={respuestaActual.datos || []}
                  onChange={(datos) => setRespuestaActual({ datos })}
                  disabled={respuestaActual.no_se}
                />
              </PasoShell>
            )}

            {paso === 5 && (
              <PasoShell
                numero={5}
                titulo="Cuando buscas uno, ¿cómo lo buscas?"
                noSe={respuestaActual.no_se}
                onNoSe={(v) => setRespuestaActual({ no_se: v })}
              >
                <OpcionesConOtro
                  opciones={[
                    { value: "nombre", label: "Por nombre" },
                    { value: "fecha", label: "Por fecha" },
                    { value: "estado", label: "Por estado" },
                    { value: "responsable", label: "Por quién es responsable" },
                  ]}
                  seleccion={respuestaActual.opciones || []}
                  otro={respuestaActual.otro || ""}
                  onChange={(opciones) => setRespuestaActual({ opciones })}
                  onOtroChange={(otro) => setRespuestaActual({ otro })}
                  disabled={respuestaActual.no_se}
                />
              </PasoShell>
            )}

            {paso === 6 && (
              <PasoShell
                numero={6}
                titulo="¿Qué te gustaría saber de un vistazo al entrar?"
                noSe={respuestaActual.no_se}
                onNoSe={(v) => setRespuestaActual({ no_se: v })}
              >
                <OpcionesConOtro
                  opciones={[
                    { value: "total", label: "Cuántos hay en total" },
                    { value: "pendientes", label: "Cuántos están pendientes" },
                    { value: "suma_mes", label: "Cuánto suman este mes" },
                    { value: "por_vencer", label: "Cuáles están por vencer" },
                  ]}
                  seleccion={respuestaActual.opciones || []}
                  otro={respuestaActual.otro || ""}
                  onChange={(opciones) => setRespuestaActual({ opciones })}
                  onOtroChange={(otro) => setRespuestaActual({ otro })}
                  disabled={respuestaActual.no_se}
                />
              </PasoShell>
            )}

            {paso === 7 && (
              <PasoShell
                numero={7}
                titulo="¿Qué pasa con cada uno después de anotarlo?"
                noSe={respuestaActual.no_se}
                onNoSe={(v) => setRespuestaActual({ no_se: v })}
              >
                <Radio.Group
                  value={respuestaActual.tipo}
                  onChange={(e) => setRespuestaActual({ tipo: e.target.value })}
                  disabled={respuestaActual.no_se}
                  className="sc-wizard-radios"
                >
                  <Radio value="solo_guardado">Solo se queda guardado</Radio>
                  <Radio value="cambia_estado">Cambia de estado</Radio>
                  <Radio value="revisado">Alguien lo revisa o lo aprueba</Radio>
                  <Radio value="se_cierra">Se cierra o se termina</Radio>
                </Radio.Group>
                {respuestaActual.tipo === "cambia_estado" && (
                  <Input
                    className="mt-2"
                    placeholder="Ej. pendiente, entregado, cancelado"
                    value={respuestaActual.estados_texto || ""}
                    onChange={(e) => setRespuestaActual({ estados_texto: e.target.value })}
                    disabled={respuestaActual.no_se}
                  />
                )}
              </PasoShell>
            )}

            {paso === 8 && (
              <PasoShell
                numero={8}
                titulo="¿Tiene que ver con algo que ya está en tu sistema?"
                noSe={respuestaActual.no_se}
                onNoSe={(v) => setRespuestaActual({ no_se: v })}
              >
                <Checkbox.Group
                  value={respuestaActual.modulos || []}
                  onChange={(modulos) => setRespuestaActual({ modulos, nada: false })}
                  disabled={respuestaActual.no_se}
                  className="sc-wizard-checks"
                >
                  {catalogo.modulos.map((m) => (
                    <Checkbox key={m.key} value={m.key}>{MODULO_LABELS[m.key] || m.key}</Checkbox>
                  ))}
                </Checkbox.Group>
                <Checkbox
                  className="mt-2"
                  checked={!!respuestaActual.nada}
                  onChange={(e) => setRespuestaActual({ nada: e.target.checked, modulos: [] })}
                  disabled={respuestaActual.no_se}
                >
                  Con nada
                </Checkbox>
              </PasoShell>
            )}

            {paso === 9 && (
              <PasoShell
                numero={9}
                titulo="¿Quién lo va a usar?"
                noSe={respuestaActual.no_se}
                onNoSe={(v) => setRespuestaActual({ no_se: v })}
              >
                <Checkbox.Group
                  value={respuestaActual.usuarios || []}
                  onChange={(ids) => {
                    const nombres = catalogo.usuarios.filter((u) => ids.includes(u.id_user)).map((u) => u.name);
                    setRespuestaActual({ usuarios: ids, usuarios_nombres: nombres });
                  }}
                  disabled={respuestaActual.no_se}
                  className="sc-wizard-checks"
                >
                  {catalogo.usuarios.map((u) => (
                    <Checkbox key={u.id_user} value={u.id_user}>{u.name}</Checkbox>
                  ))}
                </Checkbox.Group>
                <div className="sc-field mt-3">
                  <div className="sc-label">¿Hay algo que solo deba ver el dueño o un encargado?</div>
                  <TextArea
                    rows={2}
                    placeholder="Opcional"
                    value={respuestaActual.solo_dueno_texto || ""}
                    onChange={(e) => setRespuestaActual({ solo_dueno_texto: e.target.value })}
                    disabled={respuestaActual.no_se}
                  />
                </div>
              </PasoShell>
            )}

            {paso === 10 && (
              <PasoShell
                numero={10}
                titulo="¿Algo más que debamos saber?"
                noSe={respuestaActual.no_se}
                onNoSe={(v) => setRespuestaActual({ no_se: v })}
              >
                <TextArea
                  rows={3}
                  placeholder="Opcional"
                  value={respuestaActual.texto || ""}
                  onChange={(e) => setRespuestaActual({ texto: e.target.value })}
                  disabled={respuestaActual.no_se}
                  className="mb-3"
                />
                <div className="sc-label">Urgencia</div>
                <Radio.Group
                  value={respuestaActual.urgencia}
                  onChange={(e) => setRespuestaActual({ urgencia: e.target.value })}
                  disabled={respuestaActual.no_se}
                >
                  <Radio value="pronto">Lo necesito pronto</Radio>
                  <Radio value="puede_esperar">Puede esperar</Radio>
                </Radio.Group>
              </PasoShell>
            )}

            <div className="sc-wizard-nav">
              <Button onClick={atras} disabled={paso === 1}>Atrás</Button>
              <Button type="primary" onClick={siguiente} loading={guardando}>Siguiente</Button>
            </div>
          </>
        ) : (
          <ResumenFinal
            respuestas={respuestas}
            resumenInicial={resumen}
            idWizard={idWizard}
            onCorregir={irAPregunta}
            onEnviar={enviarSolicitud}
            enviando={enviando}
          />
        )}
      </div>
    </main>
  );
}

function DatosDinamicos({ datos, onChange, disabled }) {
  const actualizar = (i, patch) => {
    const nuevos = datos.map((d, idx) => (idx === i ? { ...d, ...patch } : d));
    onChange(nuevos);
  };
  const quitar = (i) => onChange(datos.filter((_, idx) => idx !== i));
  const agregar = () => onChange([...datos, { nombre: "", siempre: true }]);

  return (
    <div>
      {datos.map((d, i) => (
        <div key={i} className="sc-wizard-dato-row">
          <Input
            placeholder="Ej. nombre, teléfono, qué me vende..."
            value={d.nombre}
            onChange={(e) => actualizar(i, { nombre: e.target.value })}
            disabled={disabled}
            style={{ flex: 1 }}
          />
          <Radio.Group
            value={d.siempre ? "siempre" : "a_veces"}
            onChange={(e) => actualizar(i, { siempre: e.target.value === "siempre" })}
            disabled={disabled}
            optionType="button"
          >
            <Radio.Button value="siempre">Siempre lo tengo</Radio.Button>
            <Radio.Button value="a_veces">A veces no lo tengo</Radio.Button>
          </Radio.Group>
          <Button danger type="text" icon={<DeleteOutlined />} onClick={() => quitar(i)} disabled={disabled} />
        </div>
      ))}
      <Button onClick={agregar} disabled={disabled} className="mt-2">+ Agregar dato</Button>
    </div>
  );
}

function OpcionesConOtro({ opciones, seleccion, otro, onChange, onOtroChange, disabled }) {
  return (
    <div>
      <Checkbox.Group value={seleccion} onChange={onChange} disabled={disabled} className="sc-wizard-checks">
        {opciones.map((o) => (
          <Checkbox key={o.value} value={o.value}>{o.label}</Checkbox>
        ))}
      </Checkbox.Group>
      <Input
        className="mt-2"
        placeholder="Otro (opcional)"
        value={otro}
        onChange={(e) => onOtroChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

function ResumenFinal({ respuestas, resumenInicial, idWizard, onCorregir, onEnviar, enviando }) {
  const [resumen, setResumen] = useState(resumenInicial);
  const [cargando, setCargando] = useState(!resumenInicial);

  useEffect(() => {
    if (resumenInicial) return;
    let cancelado = false;
    wizardService.previsualizarResumen(idWizard).then((r) => {
      if (!cancelado) setResumen(r);
    }).finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idWizard]);

  const secciones = [
    { paso: 1, label: "De qué quieres llevar control" },
    { paso: 2, label: "Para qué lo necesitas" },
    { paso: 3, label: "Cómo lo llevas hoy" },
    { paso: 4, label: "Qué datos apuntas" },
    { paso: 5, label: "Cómo lo buscas" },
    { paso: 6, label: "Qué quieres ver de un vistazo" },
    { paso: 7, label: "Qué pasa después" },
    { paso: 8, label: "Si tiene que ver con algo más" },
    { paso: 9, label: "Quién lo va a usar" },
    { paso: 10, label: "Algo más" },
  ];

  return (
    <div className="sc-wizard-paso">
      <h2 className="sc-wizard-pregunta">Esto es lo que entendimos</h2>
      {cargando ? <Spin /> : <p className="sc-wizard-resumen">{resumen}</p>}

      <div className="sc-wizard-corregir">
        {secciones.map((s) => (
          <Button key={s.paso} type="link" size="small" onClick={() => onCorregir(s.paso)}>
            Corregir «{s.label}»
          </Button>
        ))}
      </div>

      <Alert
        type="info"
        showIcon
        className="mt-3 mb-3"
        message="Las funciones nuevas se cotizan aparte."
        description="Te enviaremos la cotización antes de empezar, y tú decides si seguimos."
      />

      <Button type="primary" size="large" onClick={onEnviar} loading={enviando}>Enviar solicitud</Button>
    </div>
  );
}
